import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../contexts/AuthContext";
import memoService from "../../services/memo.service";
import "./memo.css";

const categories = [
  "すべて", "未分類", "生成AI", "ChatGPT", "マーケティング", "営業",
  "副業", "目標設計", "その他",
];

// YouTubeサムネURL
const ytThumb = (videoId, quality = "hqdefault") =>
  `https://i.ytimg.com/vi/${videoId}/${quality}.jpg`;

export default function MemoPage() {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState("すべて");
  const [searchWord, setSearchWord] = useState("");
  const [memos, setMemos] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [justSharedId, setJustSharedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveQueue, setSaveQueue] = useState(new Map());

  // データ取得
  const fetchMemosAndVideos = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error } = await memoService.getVideosWithMemos(user.id);
      
      if (error) {
        throw error;
      }

      const formattedMemos = (data || []).map(video => ({
        id: video.youtube_video_id,
        videoId: video.youtube_video_id,
        title: video.title,
        category: video.category || "未分類",
        memo: video.memo || "",
        detailPath: `/videos/${video.youtube_video_id}`,
        shareUrl: `${window.location.origin}/videos/${video.youtube_video_id}`,
        thumbnail_path: video.thumbnail_path,
      }));

      setMemos(formattedMemos);
    } catch (err) {
      console.error("Error loading memos:", err);
      setError("メモの読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [user]);

  // 初期化：Supabaseからデータ取得
  useEffect(() => {
    fetchMemosAndVideos();
  }, [fetchMemosAndVideos]);

  const filtered = useMemo(() => {
    const word = searchWord.trim().toLowerCase();
    return memos.filter((m) => {
      const passCategory =
        selectedCategory === "すべて" || m.category === selectedCategory;
      const passSearch =
        !word ||
        m.title.toLowerCase().includes(word) ||
        m.memo.toLowerCase().includes(word);
      return passCategory && passSearch;
    });
  }, [memos, selectedCategory, searchWord]);

  // リアルタイム同期
  useEffect(() => {
    if (!user) return;

    const subscription = memoService.subscribeToMemoChanges(user.id, (payload) => {
      if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
        fetchMemosAndVideos();
      }
    });

    return () => {
      memoService.unsubscribeFromMemoChanges(subscription);
    };
  }, [user, fetchMemosAndVideos]);

  // デバウンス処理用タイマー
  useEffect(() => {
    const timer = setTimeout(() => {
      saveQueue.forEach(async (memo, videoId) => {
        if (user) {
          await memoService.createOrUpdateMemo(user.id, videoId, memo);
        }
      });
      setSaveQueue(new Map());
    }, 1000);

    return () => clearTimeout(timer);
  }, [saveQueue, user]);

  const handleChange = (id, value) => {
    setMemos((prev) => prev.map((m) => (m.id === id ? { ...m, memo: value } : m)));
    
    // デバウンス用のキューに追加
    setSaveQueue(prev => new Map(prev).set(id, value));
  };

  const handleSave = async (id) => {
    if (!user) {
      setError("ログインが必要です");
      return;
    }

    const target = memos.find((m) => m.id === id);
    if (!target) return;

    try {
      const { error } = await memoService.createOrUpdateMemo(
        user.id,
        target.videoId,
        target.memo || ""
      );

      if (error) {
        throw error;
      }

      alert('メモが保存されました');
    } catch (err) {
      console.error("Error saving memo:", err);
      setError("メモの保存に失敗しました");
    }
  };

  const handleShare = async (card) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: card.title,
          text: "動画メモを共有",
          url: card.shareUrl,
        });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(card.shareUrl);
      }
      setJustSharedId(card.id);
      setTimeout(() => setJustSharedId(null), 1200);
    } catch {
      // キャンセル等は無視
    }
  };

  return (
    <div className="system-page">
      <Sidebar />

      <main className="memo-wrapper">
        <div className="background-overlay"></div>

        <div className="memo-content">
          <div className="videolist-header">
            {/* 検索 */}
            <div className="memo-search-container">
              <input
                type="text"
                placeholder="メモを検索する"
                className="memo-input"
                value={searchWord}
                onChange={(e) => setSearchWord(e.target.value)}
              />
            </div>

            {/* カテゴリーバー */}
            <div className="category-bar">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-button ${
                    selectedCategory === category ? "active" : ""
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* エラー表示 */}
          {error && (
            <div className="memo-error">
              {error}
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}

          {/* ローディング表示 */}
          {loading ? (
            <div className="memo-loading">
              <div className="memo-loading-spinner"></div>
              <p>メモを読み込み中...</p>
            </div>
          ) : !user ? (
            <div className="memo-login-prompt">
              <p>メモ機能を使用するにはログインが必要です</p>
              <Link to="/login" className="memo-login-link">
                ログインページへ
              </Link>
            </div>
          ) : filtered.length === 0 ? (
            <div className="memo-empty">
              <p>{searchWord || selectedCategory !== "すべて" 
                ? "該当するメモが見つかりませんでした" 
                : "まだメモがありません"}</p>
            </div>
          ) : (
            /* メモカード一覧 */
            <section className="memo-grid">
            {filtered.map((card) => (
              <article
                key={card.id}
                className={`memo-card ${selectedCardId === card.id ? "selected" : ""}`}
                onClick={() => setSelectedCardId(card.id)}
              >
                {/* タイトル */}
                <header className="memo-card-header">
                  <h3 className="memo-card-title">{card.title}</h3>
                </header>

                {/* タイトルの下にカテゴリーバッジ＋保存ボタン */}
                <div className="memo-meta-row">
                  <span className="memo-card-chip">{card.category}</span>
                  <button
                    type="button"
                    className="memo-save-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave(card.id);
                    }}
                  >
                    保存
                  </button>
                </div>

                {/* 本文 */}
                <div className="memo-editor-wrap">
                  <textarea
                    className="memo-textarea"
                    value={card.memo}
                    onChange={(e) => handleChange(card.id, e.target.value)}
                    placeholder="メモを入力…"
                  />
                </div>

                {/* サムネ（16:9）＋ 共有ボタン（右上） */}
                <div className="memo-thumb">
                  <Link
                    to={card.detailPath}
                    className="thumb-link"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`${card.title} の詳細ページへ`}
                  >
                    <img
                      src={ytThumb(card.videoId, "hqdefault")}
                      alt={`${card.title} のサムネイル`}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = ytThumb(card.videoId, "mqdefault");
                      }}
                    />
                    <span className="thumb-overlay">
                      <span className="thumb-play-icon">▶</span>
                      詳細を見る
                    </span>
                  </Link>

                  <button
                    type="button"
                    className="thumb-share-btn"
                    title="共有"
                    aria-label="共有"
                    onClick={(e) => { e.stopPropagation(); handleShare(card); }}
                  >
                    <img src="/link.png" alt="" className="thumb-share-icon" />
                  </button>
                </div>

                {justSharedId === card.id && (
                  <div className="memo-shared-toast">リンクを共有/コピーしました</div>
                )}
              </article>
            ))}
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
